import React, { useMemo, useState } from 'react';
import { ChevronRight, Play, Search } from 'lucide-react';

type ImageUrls = Record<string, string>;
type Section = 'Filmik for Coordinators' | 'Filmik for Performers' | 'Filmik Tips';
type Guide = { id: string; title: string; section: Section; image: string; description: string };
type Props = { guideId?: string; imageUrls: ImageUrls };

const guideContent: Array<[string, string, Section, string, string]> = [
  [
    'set-up-a-project',
    'Set up a project',
    'Filmik for Coordinators',
    'setupProject',
    'Create a project, set the essentials, and invite your collaborators.',
  ],
  [
    'project-permissions',
    'Set up project permissions',
    'Filmik for Coordinators',
    'projectPermissions',
    'Give each person the right level of access for their role on the project.',
  ],
  [
    'use-the-calendar',
    'Use the calendar',
    'Filmik for Coordinators',
    'calendar',
    'Keep the production schedule, events, and important dates organized.',
  ],
  [
    'set-up-your-profile',
    'Set up your profile',
    'Filmik for Coordinators',
    'profile',
    'Make your professional information clear and easy to find.',
  ],
  [
    'use-submission-boards',
    'Use Submission Boards',
    'Filmik for Coordinators',
    'submissionBoards',
    'Build a board, review profiles, and manage the submission process.',
  ],
  [
    'set-up-media-kit',
    'Set up your Media Kit',
    'Filmik for Performers',
    'mediaKit',
    'Add the photos and videos that help you present your work.',
  ],
  [
    'use-member-directory',
    'Use the Member Directory',
    'Filmik for Performers',
    'directory',
    'Find and connect with the people you need for your next production.',
  ],
  [
    'set-up-submission-kit',
    'Set up your Submission Kit',
    'Filmik for Performers',
    'submissionKit',
    'Prepare the materials you want to share for a submission.',
  ],
  [
    'use-filmik-on-desktop',
    'Use Filmik on desktop',
    'Filmik for Performers',
    'desktop',
    'Navigate Filmik’s core tools from your desktop workspace.',
  ],
  [
    'filmik-tip',
    'Filmik Tip',
    'Filmik Tips',
    'filmikTip',
    'Small habits that make your Filmik workspace easier to use.',
  ],
];

const sections: Section[] = ['Filmik for Coordinators', 'Filmik for Performers', 'Filmik Tips'];
const borderStyle = { borderColor: 'rgba(140, 162, 194, 0.5)' };

function Crumbs({ guide }: { guide?: Guide }) {
  return (
    <>
      <hr />
      <nav
        className="flex border-b border-gray-200 bg-white px-4 py-3 sm:px-8"
        aria-label="Breadcrumb"
      >
        <ol role="list" className="flex items-center space-x-4">
          <li>
            <div className="flex items-center">
              <span className="ml-4 text-sm font-medium text-gray-500">Support</span>
            </div>
          </li>
          <li>
            <div className="flex items-center">
              <ChevronRight className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden />
              <a
                href="/support/how-to"
                className="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                How to Use
              </a>
            </div>
          </li>
          {guide && (
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden />
                <span
                  className="ml-4 truncate text-sm font-medium text-gray-500"
                  aria-current="page"
                >
                  {guide.title}
                </span>
              </div>
            </li>
          )}
        </ol>
      </nav>
    </>
  );
}

function Thumbnail({ guide }: { guide: Guide }) {
  return <img src={guide.image} alt="" className="h-full w-full object-cover" />;
}

function MainPage({ guides }: { guides: Guide[] }) {
  const [query, setQuery] = useState('');
  const visible = useMemo(() => {
    const value = query.trim().toLowerCase();
    return value ? guides.filter((guide) => guide.title.toLowerCase().includes(value)) : guides;
  }, [guides, query]);
  return (
    <>
      <Crumbs />
      <main className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-12 sm:py-10">
        <h1
          className="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl"
          style={{ lineHeight: 1.2 }}
        >
          Filmik: How to Use
        </h1>
        <form
          className="mt-6 flex items-center gap-2 pb-6"
          onSubmit={(event) => event.preventDefault()}
        >
          <label className="flex-1">
            <span className="sr-only">Search guides</span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by topic or keyword"
              className="h-10 w-full rounded-lg border border-blue-200 bg-gray-50 px-4 text-sm text-gray-800 placeholder-gray-400 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </label>
          <button
            type="submit"
            className="inline-flex h-10 items-center gap-2 rounded-md bg-blue-500 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <Search className="h-5 w-5" aria-hidden />
            <span className="hidden sm:inline">Search</span>
          </button>
        </form>
        <div className="rounded-2xl border bg-white px-5 py-7 sm:py-10" style={borderStyle}>
          <div className="px-0 sm:px-12" style={{ maxWidth: '1060px' }}>
            {sections.map((section, index) => {
              const items = visible.filter((guide) => guide.section === section);
              if (!items.length) return null;
              return (
                <section
                  key={section}
                  className={index ? 'border-t pt-8' : ''}
                  style={index ? borderStyle : undefined}
                  aria-labelledby={section.replaceAll(' ', '-').toLowerCase()}
                >
                  <h2
                    id={section.replaceAll(' ', '-').toLowerCase()}
                    className="mb-4 text-2xl font-medium tracking-tight sm:text-3xl"
                    style={{ color: '#576c89' }}
                  >
                    {section}
                  </h2>
                  <div className="grid gap-x-0 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
                    {items.map((guide) => (
                      <a
                        key={guide.id}
                        href={`/support/how-to/${guide.id}`}
                        className="group rounded-xl px-3 pb-4 pt-3 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <div
                          className="relative overflow-hidden rounded-lg"
                          style={{ aspectRatio: '16 / 9' }}
                        >
                          <Thumbnail guide={guide} />
                          <span className="absolute inset-0 flex items-center justify-center bg-gray-900/5 opacity-0 transition group-hover:opacity-100">
                            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-blue-500 shadow">
                              <Play className="ml-0.5 h-4 w-4 fill-current" aria-hidden />
                            </span>
                          </span>
                        </div>
                        <p className="mt-3 text-base font-medium text-gray-700">{guide.title}</p>
                      </a>
                    ))}
                  </div>
                </section>
              );
            })}
            {!visible.length && (
              <p className="py-10 text-center text-sm text-gray-500">
                No guides match that search.
              </p>
            )}
          </div>
        </div>
      </main>
    </>
  );
}

function DetailPage({ guide }: { guide: Guide }) {
  return (
    <>
      <Crumbs guide={guide} />
      <main className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-12 sm:py-10">
        <a
          href="/support/how-to"
          className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-blue-500 hover:text-blue-700"
        >
          <ChevronRight className="h-4 w-4 rotate-180" aria-hidden />
          All guides
        </a>
        <div
          className="rounded-2xl border bg-white px-5 py-7 sm:px-14 sm:py-10"
          style={borderStyle}
        >
          <p className="text-sm font-semibold" style={{ color: '#576c89' }}>
            {guide.section}
          </p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">
            {guide.title}
          </h1>
          <div
            className="mt-7 overflow-hidden rounded-xl bg-gray-900"
            style={{ aspectRatio: '16 / 9' }}
          >
            <div className="relative h-full w-full">
              <Thumbnail guide={guide} />
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900/20">
                <button
                  type="button"
                  className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-blue-500 shadow-lg transition hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  aria-label={`Play ${guide.title}`}
                >
                  <Play className="ml-1 h-7 w-7 fill-current" aria-hidden />
                </button>
              </div>
            </div>
          </div>
          <div className="mt-8 max-w-3xl border-t pt-6" style={borderStyle}>
            <h2 className="text-2xl font-medium" style={{ color: '#576c89' }}>
              How to use Filmik
            </h2>
            <p className="mt-3 text-base leading-7 text-gray-600">{guide.description}</p>
            <p className="mt-3 text-base leading-7 text-gray-600">
              This page is ready for a YouTube embed or a self-hosted walkthrough when the final
              video is available.
            </p>
          </div>
        </div>
      </main>
    </>
  );
}

export default function SupportHowToPage({ guideId, imageUrls }: Props) {
  const guides = useMemo(
    () =>
      guideContent.map(([id, title, section, image, description]) => ({
        id,
        title,
        section,
        image: imageUrls[image],
        description,
      })),
    [imageUrls]
  );
  const guide = guideId ? guides.find((item) => item.id === guideId) : undefined;
  return (
    <div style={{ backgroundColor: '#e9eef6', minHeight: '100%' }}>
      {guide ? <DetailPage guide={guide} /> : <MainPage guides={guides} />}
    </div>
  );
}
